import { z } from "zod";

/**
 * Регламенты агентства: одна витрина вместо Диска, сетевой папки, Miro и
 * сайта Насти (созвон с Викой 26.10.2026).
 *
 * Корпус — данные агентства, в git его нет. Здесь только форма корпуса и
 * чистые функции вокруг модели: что ей показать и как проверить, что она
 * ответила по корпусу, а не по своему разумению. Сам корпус собирается
 * `scripts/regulations-pack.ts` из папки вне репозитория и лежит в базе
 * (0036).
 */

/** Вкладки слева. Порядок — по ходу проекта, общее в конце. */
export const TOPICS = [
  { id: "presale", label: "Пресейл" },
  { id: "handover", label: "Передача в производство" },
  { id: "delivery", label: "Реализация" },
  { id: "docs", label: "Документы и оплаты" },
  { id: "closing", label: "Закрытие проекта" },
  { id: "general", label: "Роли и общие правила" },
] as const;

export type TopicId = (typeof TOPICS)[number]["id"];

/** Направление: у Вики HR Comm и Digital; общее — то, что для всех, вроде бухгалтерии. */
export const DIRECTIONS = [
  { id: "hr_comm", label: "HR Comm" },
  { id: "digital", label: "Digital" },
  { id: "common", label: "Общее" },
] as const;

export type DirectionId = (typeof DIRECTIONS)[number]["id"];

const topicIds = TOPICS.map((t) => t.id) as [TopicId, ...TopicId[]];
const directionIds = DIRECTIONS.map((d) => d.id) as [DirectionId, ...DirectionId[]];

const Slug = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, "id — латиница, цифры и дефис");

export const SourceSchema = z.object({
  /** как документ называется у агентства */
  title: z.string().min(1).max(300),
  url: z.string().url().max(1000).optional(),
  /** дата редакции, ГГГГ-ММ-ДД; нет — значит в документе её нет */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** что важно знать об источнике: «снимок листа», «дата не указана» */
  note: z.string().max(500).optional(),
});

export const SectionSchema = z.object({
  id: Slug,
  topic: z.enum(topicIds),
  direction: z.enum(directionIds),
  title: z.string().min(1).max(200),
  /** текст раздела, упрощённый markdown (см. markdown.ts) */
  body: z.string().min(1).max(30_000),
  source: SourceSchema,
  order: z.number().int().default(100),
});

/** Схема процесса: дорожки, узлы на сетке, переходы. XML собирает bpmn.ts. */
export const ProcessSpecSchema = z.object({
  id: Slug,
  topic: z.enum(topicIds),
  title: z.string().min(1).max(200),
  /** из чего собрана схема — показывается под ней */
  basedOn: z.string().max(1000),
  lanes: z.array(z.object({ id: Slug, name: z.string().min(1).max(80) })).min(1),
  nodes: z
    .array(
      z.object({
        id: Slug,
        type: z.enum(["start", "end", "task", "xor", "and"]),
        lane: Slug,
        col: z.number().int().min(0).max(40),
        name: z.string().max(200).default(""),
        /** узел, где источники расходятся: id пункта карты расхождений */
        conflict: Slug.optional(),
        /** раздел корпуса, откуда шаг */
        section: Slug.optional(),
      }),
    )
    .min(2),
  flows: z.array(z.object({ from: Slug, to: Slug, label: z.string().max(60).optional() })),
});

export const DiscrepancySchema = z.object({
  id: Slug,
  title: z.string().min(1).max(200),
  topic: z.enum(topicIds),
  variants: z
    .array(
      z.object({
        /** короткое имя источника: «Бухгалтерия», «Digital», «HR Comm, фаза 2» */
        source: z.string().min(1).max(100),
        says: z.string().min(1).max(1000),
        section: Slug.optional(),
      }),
    )
    .min(2),
  /** вопрос на решение */
  question: z.string().min(1).max(1000),
  /** кому решать: id контакта */
  owner: Slug,
});

export const ContactSchema = z.object({
  id: Slug,
  name: z.string().min(1).max(120),
  role: z.string().min(1).max(200),
  /** за что отвечает — по этому модель выбирает, к кому отправить */
  covers: z.string().min(1).max(500),
  /** к кому идти, если модель не назвала никого: владелец регламентов */
  fallback: z.boolean().default(false),
});

export const CorpusSchema = z.object({
  /** когда корпус собран, ГГГГ-ММ-ДД */
  builtAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  examples: z.array(z.string().min(1).max(200)).max(6),
  contacts: z.array(ContactSchema).min(1),
  sections: z.array(SectionSchema).min(1),
  processes: z.array(ProcessSpecSchema),
  discrepancies: z.array(DiscrepancySchema),
  /**
   * Заметки — не регламент: показываются на странице расхождений, модели
   * не уходят (например, лист «рекомендации ИИ» из регламента Digital).
   */
  notes: z
    .array(
      z.object({
        title: z.string().min(1).max(200),
        body: z.string().min(1).max(10_000),
        source: SourceSchema,
      }),
    )
    .default([]),
  /** что ещё не прислали — показывается внизу витрины */
  missing: z.array(z.string().max(300)).default([]),
});

export type Source = z.infer<typeof SourceSchema>;
export type Section = z.infer<typeof SectionSchema>;
export type ProcessSpec = z.infer<typeof ProcessSpecSchema>;
export type Discrepancy = z.infer<typeof DiscrepancySchema>;
export type Contact = z.infer<typeof ContactSchema>;
export type Corpus = z.infer<typeof CorpusSchema>;

/**
 * Проверка ссылок внутри корпуса: схемы и расхождения ссылаются на разделы,
 * узлы — на дорожки, переходы — на узлы. Битая ссылка в витрине — это
 * кнопка, ведущая в никуда, поэтому корпус с ней не собирается вовсе.
 */
export function corpusProblems(c: Corpus): string[] {
  const out: string[] = [];
  const sections = new Set<string>();
  for (const s of c.sections) {
    if (sections.has(s.id)) out.push(`раздел ${s.id} встречается дважды`);
    sections.add(s.id);
  }
  const contacts = new Set(c.contacts.map((p) => p.id));
  const discrepancies = new Set(c.discrepancies.map((d) => d.id));

  for (const d of c.discrepancies) {
    if (!contacts.has(d.owner)) out.push(`расхождение ${d.id}: нет контакта ${d.owner}`);
    for (const v of d.variants) {
      if (v.section && !sections.has(v.section))
        out.push(`расхождение ${d.id}: нет раздела ${v.section}`);
    }
  }

  for (const p of c.processes) {
    const lanes = new Set(p.lanes.map((l) => l.id));
    const nodes = new Set<string>();
    const cells = new Set<string>();
    for (const n of p.nodes) {
      if (nodes.has(n.id)) out.push(`схема ${p.id}: узел ${n.id} дважды`);
      nodes.add(n.id);
      if (!lanes.has(n.lane)) out.push(`схема ${p.id}: узел ${n.id} в несуществующей дорожке`);
      const cell = `${n.lane}:${n.col}`;
      if (cells.has(cell)) out.push(`схема ${p.id}: два узла в клетке ${cell}`);
      cells.add(cell);
      if (n.section && !sections.has(n.section))
        out.push(`схема ${p.id}: узел ${n.id} ссылается на нет раздела ${n.section}`);
      if (n.conflict && !discrepancies.has(n.conflict))
        out.push(`схема ${p.id}: узел ${n.id} ссылается на нет расхождения ${n.conflict}`);
    }
    for (const f of p.flows) {
      if (!nodes.has(f.from) || !nodes.has(f.to))
        out.push(`схема ${p.id}: переход ${f.from} → ${f.to} на несуществующий узел`);
    }
  }
  return out;
}

export function topicLabel(id: string): string {
  return TOPICS.find((t) => t.id === id)?.label ?? id;
}

export function directionLabel(id: string): string {
  return DIRECTIONS.find((d) => d.id === id)?.label ?? id;
}

/** Разделы вкладки в порядке показа. */
export function sectionsOf(c: Corpus, topic: TopicId): Section[] {
  return c.sections
    .filter((s) => s.topic === topic)
    .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title, "ru"));
}

/** «01.12.2021» из «2021-12-01»; пусто — пусто. */
export function ruDate(iso: string | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// ---------------------------------------------------------------------
// Вопрос к регламентам
// ---------------------------------------------------------------------

/**
 * Что видит модель: каждый раздел под своим id, с направлением и
 * источником, а за ними — карта расхождений. Корпус небольшой (десятки
 * тысяч знаков), поэтому отдаём целиком: поиск по кускам ошибался бы на
 * вопросах, ответ на которые собирается из трёх документов сразу.
 */
export function corpusContext(c: Corpus): string {
  const parts: string[] = [];
  for (const t of TOPICS) {
    for (const s of sectionsOf(c, t.id)) {
      const src = [s.source.title, s.source.date ? `ред. ${ruDate(s.source.date)}` : ""]
        .filter(Boolean)
        .join(", ");
      parts.push(
        `<section id="${s.id}" tab="${t.label}" direction="${directionLabel(s.direction)}" source="${src}">\n## ${s.title}\n${s.body.trim()}\n</section>`,
      );
    }
  }
  if (c.discrepancies.length) {
    const lines = c.discrepancies.map((d) => {
      const vs = d.variants.map((v) => `   - ${v.source}: ${v.says}`).join("\n");
      return `- [${d.id}] ${d.title}\n${vs}\n   Решает: ${d.owner}`;
    });
    parts.push(
      `<discrepancies>\nМеста, где источники противоречат друг другу:\n${lines.join("\n")}\n</discrepancies>`,
    );
  }
  const people = c.contacts.map((p) => `- ${p.id}: ${p.name}, ${p.role}. Отвечает за: ${p.covers}`);
  parts.push(`<contacts>\n${people.join("\n")}\n</contacts>`);
  return parts.join("\n\n");
}

export const ANSWER_TOOL = "answer_from_regulations";

/** Что модель возвращает. Держим в одном месте с проверкой ответа. */
export const RawAnswerSchema = z.object({
  coverage: z.enum(["full", "partial", "none"]),
  answer: z.string().max(6000),
  sections: z.array(z.string().max(80)).max(12).default([]),
  conflicts: z.array(z.string().max(80)).max(8).default([]),
  ask: z.string().max(80).nullish(),
  gap: z.string().max(1000).nullish(),
  draft_question: z.string().max(2000).nullish(),
});

export type RawAnswer = z.infer<typeof RawAnswerSchema>;

export interface RegulationsAnswer {
  coverage: "full" | "partial" | "none";
  answer: string;
  /** разделы, на которые опирается ответ, — только существующие */
  sections: { id: string; title: string; topic: TopicId }[];
  conflicts: { id: string; title: string }[];
  /** к кому идти, если в регламентах пусто или противоречиво */
  ask: Contact | null;
  /** что именно не описано */
  gap: string | null;
  /** готовый вопрос, который можно отправить */
  draftQuestion: string | null;
}

/**
 * Проверяет ответ модели против корпуса.
 *
 * Ссылки на разделы, которых нет, выбрасываются: витрина обещает «ответ
 * только по регламентам со ссылками», и ссылка в никуда это обещание
 * ломает. Если после чистки опоры не осталось, а модель заявила полный
 * ответ, — понижаем до «не описано»: без ссылки ответ ничем не подтверждён.
 *
 * Когда описано не всё, а модель не назвала, к кому идти, ставим владельца
 * расхождения или владельца регламентов (`fallback`) — человек не должен
 * остаться с «не знаю» без следующего шага.
 */
export function checkAnswer(raw: RawAnswer, c: Corpus): RegulationsAnswer {
  const byId = new Map(c.sections.map((s) => [s.id, s]));
  const sections = [...new Set(raw.sections)]
    .map((id) => byId.get(id))
    .filter((s): s is Section => !!s)
    .map((s) => ({ id: s.id, title: s.title, topic: s.topic }));

  const dById = new Map(c.discrepancies.map((d) => [d.id, d]));
  const conflicts = [...new Set(raw.conflicts)]
    .map((id) => dById.get(id))
    .filter((d): d is Discrepancy => !!d)
    .map((d) => ({ id: d.id, title: d.title }));

  let coverage = raw.coverage;
  if (coverage !== "none" && sections.length === 0) coverage = "none";

  const contacts = new Map(c.contacts.map((p) => [p.id, p]));
  let ask = raw.ask ? (contacts.get(raw.ask) ?? null) : null;
  if (!ask && (coverage !== "full" || conflicts.length)) {
    const owner = conflicts.length ? dById.get(conflicts[0].id)?.owner : undefined;
    ask = contacts.get(owner ?? "") ?? c.contacts.find((p) => p.fallback) ?? c.contacts[0] ?? null;
  }

  return {
    coverage,
    answer: raw.answer.trim(),
    sections,
    conflicts,
    ask: coverage === "full" && !conflicts.length ? null : ask,
    gap: coverage === "full" ? null : raw.gap?.trim() || null,
    draftQuestion:
      coverage === "full" && !conflicts.length ? null : raw.draft_question?.trim() || null,
  };
}

export const REGULATIONS_SYSTEM_PROMPT = `Ты — справочная по регламентам агентства Braining Group (направления HR Comm и Digital). Отвечаешь сотрудникам на вопросы о том, как устроены процессы: пресейл, передача проекта в производство, реализация, документы и оплаты, закрытие проекта.

Правила, без исключений:
1. Отвечай ТОЛЬКО по тексту регламентов из сообщения пользователя (теги <section>). Не додумывай, не обобщай «как обычно принято в агентствах», не советуй от себя.
2. В ответе ссылайся на разделы: перечисли их id в поле sections. Без опоры на раздел утверждать нельзя.
3. Если ответа в регламентах нет или он неполный — так прямо и скажи (coverage = none или partial), в поле gap коротко опиши, что именно не описано. Назови, у кого уточнить (поле ask — id из <contacts>): документы, оплаты, калькулятор, бухгалтерия, закрывающие — операционный директор; процесс, роли, порядок шагов, передача — руководитель направления. Составь готовый вопрос этому человеку (draft_question): коротко, по делу, от лица сотрудника, на «вы».
4. Если по вопросу регламенты противоречат друг другу (см. <discrepancies> или видно из текста) — приведи оба варианта с источниками, укажи id расхождения в conflicts, не выбирай за агентство. Назови, кто решает, и составь вопрос.
5. Если правило разное для HR Comm и Digital — скажи, где какое. Если направление спрашивающего неизвестно, приведи оба.
6. Ивенты (Braining Events) не входят в эти регламенты. Регламент взаимодействия с бухгалтерией писался в том числе под ивенты, но действует для всех.
7. Пиши по-русски, коротко и по шагам: кто делает, что, куда, в какой срок. Названия писем, папок и сроки — дословно из регламента. Markdown: короткие абзацы и списки «- », жирный только для ключевых сроков и названий. Без вступлений и без «надеюсь, помог».`;

export function userPrompt(question: string, context: string): string {
  return `Регламенты агентства:\n\n${context}\n\n---\nВопрос сотрудника: ${question}`;
}

export function answerToolSchema() {
  return {
    name: ANSWER_TOOL,
    description: "Ответ на вопрос сотрудника строго по регламентам агентства.",
    parameters: {
      type: "object",
      properties: {
        coverage: {
          type: "string",
          enum: ["full", "partial", "none"],
          description:
            "full — регламенты отвечают полностью; partial — частично; none — не описано",
        },
        answer: {
          type: "string",
          description:
            "Ответ по регламентам, markdown. При none — одна-две фразы, что в регламентах этого нет.",
        },
        sections: {
          type: "array",
          items: { type: "string" },
          description: "id разделов, на которые опирается ответ",
        },
        conflicts: {
          type: "array",
          items: { type: "string" },
          description: "id расхождений из <discrepancies>, которые касаются вопроса",
        },
        ask: {
          type: "string",
          description: "id контакта, у кого уточнить; пусто, если ответ полный и без противоречий",
        },
        gap: { type: "string", description: "что именно не описано в регламентах" },
        draft_question: {
          type: "string",
          description: "готовый вопрос этому человеку, если ответ неполный или есть противоречие",
        },
      },
      required: ["coverage", "answer", "sections"],
    },
  };
}
